import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Modal, DatePicker, Spin, Pagination } from "antd";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";

const groupByDate = (photos = []) => {
  const map = new Map();
  photos.forEach((p) => {
    const key = (() => {
      if (!p.date) return "Unknown";
      const parsed = dayjs(p.date);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "Unknown";
    })();
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  });
  // Sorted by date desc, unknown at end
  const sortedKeys = Array.from(map.keys()).sort((a, b) => {
    if (a === "Unknown") return 1;
    if (b === "Unknown") return -1;
    if (a === b) return 0;
    return a > b ? -1 : 1;
  });
  return sortedKeys.map((k) => ({ date: k, items: map.get(k) }));
};

const DailyReportsPhotosModal = ({
  open,
  onClose,
  photos,
  loading,
  onSearchDate,
  pagination,
  onPageChange,
  searchDate,
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [pendingPageNavigation, setPendingPageNavigation] = useState(null);

  const grouped = useMemo(() => groupByDate(photos), [photos]);

  useEffect(() => {
    if (pendingPageNavigation === 'next' && photos.length > 0) {
      setImageLoading(true);
      setCurrentPhotoIndex(0);
      setPendingPageNavigation(null);
    } else if (pendingPageNavigation === 'prev' && photos.length > 0) {
      setImageLoading(true);
      setCurrentPhotoIndex(photos.length - 1);
      setPendingPageNavigation(null);
    }
  }, [photos, pendingPageNavigation]);

  const handlePrevious = useCallback(() => {
    if (currentPhotoIndex !== null) {
      if (currentPhotoIndex > 0) {
        setImageLoading(true);
        setCurrentPhotoIndex(currentPhotoIndex - 1);
      } else if (pagination?.page > 1) {
        setPendingPageNavigation('prev');
        onPageChange?.(pagination.page - 1, pagination.pageSize);
      }
    }
  }, [currentPhotoIndex, pagination, onPageChange]);

  const handleNext = useCallback(() => {
    if (currentPhotoIndex !== null) {
      if (currentPhotoIndex < photos.length - 1) {
        setImageLoading(true);
        setCurrentPhotoIndex(currentPhotoIndex + 1);
      } else {
        const currentPage = pagination?.page || 1;
        const pageSize = pagination?.pageSize || 48;
        const total = pagination?.total || 0;
        const totalPages = Math.ceil(total / pageSize);
        
        if (currentPage < totalPages) {
          setPendingPageNavigation('next');
          onPageChange?.(currentPage + 1, pageSize);
        }
      }
    }
  }, [currentPhotoIndex, photos.length, pagination, onPageChange]);

  // Keyboard navigation
  useEffect(() => {
    if (currentPhotoIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      } else if (e.key === "Escape") {
        setCurrentPhotoIndex(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentPhotoIndex, handlePrevious, handleNext]);

  return (
    <Modal
      title="All Photos"
      open={open}
      onCancel={onClose}
      afterClose={() => {
        setCurrentPhotoIndex(null);
        setImageLoading(false);
        setPendingPageNavigation(null);
      }}
      footer={null}
      width={900}
    >
      <div className="flex items-center gap-2 mb-3">
        <DatePicker
          allowClear
          format={DATE_PICKER_FORMAT}
          value={searchDate || null}
          onChange={(d) => onSearchDate?.(d || null)}
          placeholder="Filter by date"
        />
      </div>

      {loading ? (
        <div className="w-full flex items-center justify-center py-10">
          <Spin />
        </div>
      ) : (
        <div className="space-y-6 max-h-[60vh] overflow-auto pr-2">
          {grouped.map((g) => (
            <div key={g.date}>
              <div className="sticky top-0 z-10 mb-3 bg-white/80 backdrop-blur">
                <div className="flex items-center">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="mx-3 px-3 py-1 text-[11px] font-semibold tracking-wide uppercase text-gray-700 bg-white rounded-full border shadow-sm">
                    {(g.date && g.date !== "Unknown"
                      ? dayjs(g.date).format("DD MMM YYYY ddd")
                      : "Unknown Date").toUpperCase()}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8">
                {g.items.map((p, idx) => (
                  <button
                    key={`${g.date}-${idx}-${p.url}`}
                    className="group text-left"
                    onClick={() => {
                      const photoIndex = photos.findIndex((photo) => photo.url === p.url);
                      if (photoIndex !== -1) {
                        setImageLoading(true);
                        setCurrentPhotoIndex(photoIndex);
                      }
                    }}
                  >
                    <div className="w-full aspect-square overflow-hidden rounded shadow-sm bg-gray-100">
                      <img
                        src={p.url}
                        alt={p.filename || "photo"}
                        className="w-full h-full object-cover group-hover:opacity-90"
                      />
                    </div>
                    <div className="mt-1 text-xs text-gray-500 truncate">
                      {p.filename}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {grouped.length === 0 && (
            <div className="text-sm text-gray-500">No photos found.</div>
          )}
          <div className="sticky bottom-0 z-10 bg-white/90 backdrop-blur border-t py-2">
            <div className="mx-auto w-fit">
              <Pagination
                size="small"
                simple
                current={pagination?.page || 1}
                pageSize={pagination?.pageSize || 48}
                total={pagination?.total || 0}
                showSizeChanger={false}
                onChange={(p, ps) => onPageChange?.(p, ps)}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        open={currentPhotoIndex !== null}
        onCancel={() => setCurrentPhotoIndex(null)}
        footer={null}
        centered
        width={800}
        closeIcon={
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/75 shadow-lg">
            <CloseOutlined className="text-white text-lg" />
          </span>
        }
      >
        <div className="relative p-0 bg-black flex items-center justify-center max-h-[70vh]">
          {currentPhotoIndex !== null && photos[currentPhotoIndex] && (
            <>
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Spin size="large" />
                </div>
              )}
              <img
                src={photos[currentPhotoIndex].url}
                alt={photos[currentPhotoIndex].filename || "preview"}
                className="max-h-[70vh] max-w-full object-contain"
                onLoad={() => setImageLoading(false)}
                style={{ opacity: imageLoading ? 0 : 1, transition: 'opacity 0.2s' }}
              />
              
              {/* Left */}
              {(currentPhotoIndex > 0 || (pagination?.page || 1) > 1) && (
                <button
                  onClick={handlePrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/75 hover:bg-black/90 shadow-lg transition-all"
                  aria-label="Previous photo"
                >
                  <LeftOutlined className="text-white text-lg" />
                </button>
              )}
              
              {/* Right */}
              {(() => {
                const currentPage = pagination?.page || 1;
                const pageSize = pagination?.pageSize || 48;
                const total = pagination?.total || 0;
                const totalPages = Math.ceil(total / pageSize);
                const hasNextPage = currentPage < totalPages;
                const hasNextPhoto = currentPhotoIndex < photos.length - 1;
                
                return (hasNextPhoto || hasNextPage) && (
                  <button
                    onClick={handleNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/75 hover:bg-black/90 shadow-lg transition-all"
                    aria-label="Next photo"
                  >
                    <RightOutlined className="text-white text-lg" />
                  </button>
                );
              })()}
              
              {/* counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/75 shadow-lg">
                <span className="text-white text-sm font-medium">
                  {(() => {
                    const currentPage = pagination?.page || 1;
                    const pageSize = pagination?.pageSize || 48;
                    const total = pagination?.total || 0;
                    const globalIndex = (currentPage - 1) * pageSize + currentPhotoIndex + 1;
                    return `${globalIndex} / ${total}`;
                  })()}
                </span>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Modal>
  );
};

export default DailyReportsPhotosModal;
